/**
 * W3LE Material UI Theme
 * Based on the Evidencer design system
 */
import { createTheme } from '@mui/material/styles';

// W3LE Color Palette
export const w3leColors = {
  primary: {
    main: '#1F51FF',
    light: '#5E76FF',
    dark: '#0033cc',
    contrastText: '#ffffff',
  },
  secondary: {
    main: '#2ecc71',
    light: '#5edaa2',
    dark: '#19857b',
    contrastText: '#ffffff',
  },
  polygon: {
    main: '#8247e5',
    light: '#a47ef2',
    dark: '#5e31a8',
  },
  metamask: {
    main: '#F6851B',
    light: '#f8a04a',
    dark: '#c56711',
  },
  background: {
    default: '#f9f9f9',
    paper: '#ffffff',
    subtle: '#e0e7ff',
  },
  text: {
    primary: '#333333',
    secondary: '#666666',
    disabled: '#888888',
  },
  badge: {
    administrative: { bg: '#f0e68c', text: '#655e0f' },
    nft: { bg: '#c6f6d5', text: '#22543d' },
    utility: { bg: '#d6bcfa', text: '#553c9a' },
    role: { bg: '#fed7aa', text: '#7c2d12' },
    type: { bg: '#bfdbfe', text: '#1e3a8a' },
    scope: { bg: '#c7d2fe', text: '#3730a3' },
  },
};

// Create Material UI theme
const theme = createTheme({
  palette: {
    mode: 'light',
    primary: w3leColors.primary,
    secondary: w3leColors.secondary,
    background: w3leColors.background,
    text: w3leColors.text,
    success: {
      main: '#2ecc71',
      light: '#5edaa2',
      dark: '#1e8449',
    },
    error: {
      main: '#e74c3c',
      light: '#ec7063',
      dark: '#c0392b',
    },
    warning: {
      main: '#f39c12',
      light: '#f5b041',
      dark: '#d68910',
    },
    info: {
      main: '#3498db',
      light: '#5dade2',
      dark: '#2874a6',
    },
  },
  typography: {
    fontFamily: "'Inter', 'Roboto', 'Segoe UI', sans-serif",
    h1: {
      fontSize: '3.75rem',
      fontWeight: 800,
      lineHeight: 1.2,
    },
    h2: {
      fontSize: '2.75rem',
      fontWeight: 700,
      lineHeight: 1.3,
    },
    h3: {
      fontSize: '2rem',
      fontWeight: 600,
      lineHeight: 1.4,
    },
    h4: {
      fontSize: '1.5rem',
      fontWeight: 600,
      lineHeight: 1.5,
    },
    h5: {
      fontSize: '1.25rem',
      fontWeight: 500,
      lineHeight: 1.5,
    },
    h6: {
      fontSize: '1rem',
      fontWeight: 500,
      lineHeight: 1.5,
    },
    body1: {
      fontSize: '1rem',
      lineHeight: 1.6,
    },
    body2: {
      fontSize: '0.875rem',
      lineHeight: 1.6,
    },
    button: {
      textTransform: 'none',
      fontWeight: 500,
    },
  },
  shape: {
    borderRadius: 8,
  },
  shadows: [
    'none',
    '0 2px 4px rgba(0,0,0,0.08)',
    '0 4px 6px rgba(0,0,0,0.1)',
    '0 6px 12px rgba(0,0,0,0.12)',
    '0 8px 16px rgba(0,0,0,0.14)',
    '0 10px 20px rgba(0,0,0,0.16)',
    '0 12px 24px rgba(0,0,0,0.18)',
    '0 14px 28px rgba(0,0,0,0.2)',
    '0 16px 32px rgba(0,0,0,0.22)',
    '0 18px 36px rgba(0,0,0,0.24)',
    '0 20px 40px rgba(0,0,0,0.26)',
    '0 22px 44px rgba(0,0,0,0.28)',
    '0 24px 48px rgba(0,0,0,0.3)',
    '0 26px 52px rgba(0,0,0,0.32)',
    '0 28px 56px rgba(0,0,0,0.34)',
    '0 30px 60px rgba(0,0,0,0.36)',
    '0 32px 64px rgba(0,0,0,0.38)',
    '0 34px 68px rgba(0,0,0,0.4)',
    '0 36px 72px rgba(0,0,0,0.42)',
    '0 38px 76px rgba(0,0,0,0.44)',
    '0 40px 80px rgba(0,0,0,0.46)',
    '0 42px 84px rgba(0,0,0,0.48)',
    '0 44px 88px rgba(0,0,0,0.5)',
    '0 46px 92px rgba(0,0,0,0.52)',
    '0 48px 96px rgba(0,0,0,0.54)',
  ],
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          padding: '10px 24px',
          fontSize: '0.95rem',
          boxShadow: 'none',
          transition: 'all 0.3s ease',
          '&:hover': {
            transform: 'translateY(-2px)',
            boxShadow: '0 4px 12px rgba(31, 81, 255, 0.3)',
          },
        },
        contained: {
          '&:hover': {
            boxShadow: '0 6px 16px rgba(31, 81, 255, 0.4)',
          },
        },
        outlined: {
          borderWidth: '2px',
          '&:hover': {
            borderWidth: '2px',
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
          transition: 'all 0.3s ease',
          '&:hover': {
            transform: 'translateY(-4px)',
            boxShadow: '0 8px 16px rgba(0,0,0,0.15)',
          },
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: 8,
            '&:hover fieldset': {
              borderColor: w3leColors.primary.main,
            },
            '&.Mui-focused fieldset': {
              borderColor: w3leColors.primary.main,
              borderWidth: '2px',
            },
          },
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 16,
          fontWeight: 600,
          fontSize: '0.75rem',
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',
        },
        elevation1: {
          boxShadow: '0 2px 4px rgba(0,0,0,0.08)',
        },
        elevation2: {
          boxShadow: '0 4px 8px rgba(0,0,0,0.1)',
        },
      },
    },
  },
});

export default theme;
